// Vector decorative motifs — transparent, print-crisp, identical in editor/thumbnail/export.
// color drives currentColor. Each motif fills its box (preserveAspectRatio meet).

const Leaf = ({ t }) => (
  <path transform={t} d="M0 0 C6 -7 17 -7 26 0 C17 7 6 7 0 0Z" fill="currentColor" opacity="0.9" />
);

const Flower = ({ x, y, r = 7 }) => (
  <g transform={`translate(${x} ${y})`}>
    {[0, 72, 144, 216, 288].map((a) => (
      <ellipse key={a} transform={`rotate(${a})`} cx="0" cy={-r} rx={r * 0.55} ry={r} fill="currentColor" opacity="0.55" />
    ))}
    <circle r={r * 0.5} fill="currentColor" />
  </g>
);

const branchLeaves = (n, x0, y0, dx, dy, curve) =>
  Array.from({ length: n }).map((_, i) => {
    const p = i / (n - 1);
    const x = x0 + dx * p;
    const y = y0 + dy * p + curve * Math.sin(p * Math.PI);
    const rot = (i % 2 === 0 ? -35 : 35) - dy * 0.4;
    return <Leaf key={i} t={`translate(${x} ${y}) rotate(${rot}) scale(0.8)`} />;
  });

const MOTIFS = {
  leaf_branch: {
    box: "0 0 260 70",
    render: () => (
      <g stroke="currentColor" fill="none">
        <path d="M8 35 Q130 22 252 35" strokeWidth="1.4" />
        <g stroke="none">
          {branchLeaves(9, 20, 35, 220, 0, -14)}
          {branchLeaves(8, 30, 35, 205, 0, 14)}
        </g>
      </g>
    ),
  },
  sprig: {
    box: "0 0 90 140",
    render: () => (
      <g>
        <path d="M45 138 Q45 70 45 8" stroke="currentColor" strokeWidth="1.3" fill="none" />
        <g stroke="none">
          {Array.from({ length: 6 }).map((_, i) => {
            const y = 120 - i * 20;
            return (
              <g key={i}>
                <Leaf t={`translate(45 ${y}) rotate(-50) scale(0.7)`} />
                <Leaf t={`translate(45 ${y - 8}) rotate(230) scale(0.7)`} />
              </g>
            );
          })}
        </g>
      </g>
    ),
  },
  leaf_divider: {
    box: "0 0 200 34",
    render: () => (
      <g>
        <line x1="20" y1="17" x2="82" y2="17" stroke="currentColor" strokeWidth="1" />
        <line x1="118" y1="17" x2="180" y2="17" stroke="currentColor" strokeWidth="1" />
        <g stroke="none">
          <Leaf t="translate(88 17) rotate(200) scale(0.7)" />
          <Leaf t="translate(112 17) rotate(-20) scale(0.7)" />
          <circle cx="100" cy="17" r="3" fill="currentColor" />
        </g>
      </g>
    ),
  },
  dots_divider: {
    box: "0 0 160 20",
    render: () => (
      <g fill="currentColor" stroke="currentColor">
        <line x1="30" y1="10" x2="66" y2="10" strokeWidth="0.8" />
        <line x1="94" y1="10" x2="130" y2="10" strokeWidth="0.8" />
        <circle cx="20" cy="10" r="2" />
        <circle cx="140" cy="10" r="2" />
        <rect x="76" y="6" width="8" height="8" transform="rotate(45 80 10)" fill="none" strokeWidth="1" />
      </g>
    ),
  },
  wreath: {
    box: "0 0 200 200",
    render: () => (
      <g stroke="none">
        {Array.from({ length: 26 }).map((_, i) => {
          const a = (i / 26) * 360;
          const rad = (a * Math.PI) / 180;
          const x = 100 + Math.cos(rad) * 78;
          const y = 100 + Math.sin(rad) * 78;
          return <Leaf key={i} t={`translate(${x} ${y}) rotate(${a + 90}) scale(0.72)`} />;
        })}
        <Flower x={100} y={20} r={9} />
        <Flower x={100} y={180} r={9} />
      </g>
    ),
  },
  floral_corner: {
    box: "0 0 200 200",
    render: () => (
      <g>
        <path d="M18 182 Q70 150 120 60" stroke="currentColor" strokeWidth="1.2" fill="none" />
        <g stroke="none">
          {Array.from({ length: 7 }).map((_, i) => {
            const p = i / 6;
            const x = 18 + (102 * p);
            const y = 182 - (122 * p) - 8 * Math.sin(p * 3);
            return <Leaf key={i} t={`translate(${x} ${y}) rotate(${-60 - i * 8}) scale(0.85)`} />;
          })}
          <Flower x={40} y={160} r={12} />
          <Flower x={70} y={120} r={15} />
          <Flower x={100} y={82} r={10} />
        </g>
      </g>
    ),
  },
  rose_divider: {
    box: "0 0 220 60",
    render: () => (
      <g>
        <path d="M10 40 Q60 46 100 30 Q140 14 210 22" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.7" />
        <g stroke="none">
          <Leaf t="translate(60 40) rotate(200) scale(0.8)" />
          <Leaf t="translate(150 26) rotate(-10) scale(0.8)" />
          <Flower x={90} y={30} r={13} />
          <Flower x={118} y={30} r={13} />
          <Flower x={104} y={20} r={10} />
        </g>
      </g>
    ),
  },
  flourish: {
    box: "0 0 220 40",
    render: () => (
      <g fill="none" stroke="currentColor" strokeWidth="1.2">
        <path d="M110 20 C90 6 70 6 55 14 C40 22 48 34 62 30 C74 26 70 12 55 14" />
        <path d="M110 20 C130 6 150 6 165 14 C180 22 172 34 158 30 C146 26 150 12 165 14" />
        <circle cx="110" cy="20" r="2.5" fill="currentColor" />
        <path d="M20 20 H86" strokeWidth="0.8" />
        <path d="M134 20 H200" strokeWidth="0.8" />
      </g>
    ),
  },
  monogram: {
    box: "0 0 200 240",
    render: () => (
      <g fill="none" stroke="currentColor" strokeWidth="1.3">
        <ellipse cx="100" cy="120" rx="78" ry="108" />
        <ellipse cx="100" cy="120" rx="70" ry="100" strokeWidth="0.6" />
        <g stroke="none">
          <Flower x={100} y={16} r={8} />
          <Flower x={100} y={224} r={8} />
        </g>
      </g>
    ),
  },
  crest: {
    box: "0 0 200 240",
    render: () => (
      <g stroke="currentColor" fill="none">
        <path d="M100 214 C50 190 40 150 44 96 C64 92 88 84 100 72 C112 84 136 92 156 96 C160 150 150 190 100 214Z" strokeWidth="1.5" />
        <path d="M100 202 C60 182 52 148 55 104 C72 100 90 94 100 84 C110 94 128 100 145 104 C148 148 140 182 100 202Z" strokeWidth="0.7" />
        <g stroke="none">
          {Array.from({ length: 6 }).map((_, i) => (
            <Leaf key={"l" + i} t={`translate(${44 - i * 3} ${96 - i * 14}) rotate(${210 + i * 6}) scale(0.7)`} />
          ))}
          {Array.from({ length: 6 }).map((_, i) => (
            <Leaf key={"r" + i} t={`translate(${156 + i * 3} ${96 - i * 14}) rotate(${-30 - i * 6}) scale(0.7)`} />
          ))}
        </g>
        <path d="M84 60 L100 44 L116 60Z M92 44 h16" strokeWidth="1.4" />
      </g>
    ),
  },
  deco_frame: {
    box: "0 0 200 280",
    render: () => (
      <g fill="none" stroke="currentColor">
        <rect x="14" y="14" width="172" height="252" strokeWidth="1.4" />
        <rect x="22" y="22" width="156" height="236" strokeWidth="0.6" />
        {[[14, 14, 1, 1], [186, 14, -1, 1], [14, 266, 1, -1], [186, 266, -1, -1]].map(([x, y, sx, sy], i) => (
          <path key={i} transform={`translate(${x} ${y}) scale(${sx} ${sy})`} d="M0 34 L0 0 L34 0 M8 8 L26 8 M8 8 L8 26" strokeWidth="1.3" />
        ))}
      </g>
    ),
  },
  arch: {
    box: "0 0 200 280",
    render: () => (
      <g fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M24 272 L24 96 A76 76 0 0 1 176 96 L176 272" />
      </g>
    ),
  },
};

export default function DecorSvg({ motif, color = "#000", className = "", style }) {
  const m = MOTIFS[motif] || MOTIFS.flourish;
  return (
    <svg viewBox={m.box} preserveAspectRatio="xMidYMid meet" className={className}
      style={{ width: "100%", height: "100%", color, display: "block", ...style }}>
      {m.render()}
    </svg>
  );
}

export const DECOR_MOTIFS = Object.keys(MOTIFS);
